import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Button, Chip, Stack, Divider, Tooltip,
} from '@mui/material';
import {
  AutoAwesome, EditNote, PlaylistAdd, CheckCircle, Cancel, OpenInNew,
} from '@mui/icons-material';
import DetailDialog from '../DetailDialog';
import type { DraftBlock } from '../../lib/chatTypes';
import { getEntityDraftFields, autoDraftFields } from '../../lib/entityForms';
import { arabicEntityLabel, arabicColumnLabel } from '../../lib/columnLabels';

interface Props {
  draft: DraftBlock;
  onApplied: (row: any) => void;
  onCancelled: () => void;
}

// A concise preview of the proposed values (skips media URLs / long text).
const previewFields = (fields: Record<string, any>, isRTL: boolean) =>
  Object.entries(fields)
    .filter(([k, v]) => v != null && v !== '' && !['id', 'created_at', 'updated_at'].includes(k))
    .slice(0, 6)
    .map(([k, v]) => {
      const label = isRTL ? arabicColumnLabel(k) : k;
      let val = typeof v === 'object' ? JSON.stringify(v) : String(v);
      if (/^https?:\/\//.test(val)) val = val.split('/').pop() || val;
      if (val.length > 60) val = val.slice(0, 60) + '…';
      return { label, val };
    });

const AgentDraftCard: React.FC<Props> = ({ draft, onApplied, onCancelled }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const isEdit = draft.action === 'update';
  const fields = getEntityDraftFields(draft.entity, isRTL) || autoDraftFields(draft.fields, isRTL);
  const entityLabel = arabicEntityLabel(draft.entity);
  const preview = previewFields(draft.fields, isRTL);

  const applied = draft.status === 'applied';
  const cancelled = draft.status === 'cancelled';

  const goToRecord = () => {
    if (!draft.route) return;
    navigate(draft.route.list);
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        mt: 1.5, p: 1.5, borderRadius: 2,
        borderColor: applied ? 'success.main' : cancelled ? 'divider' : 'secondary.main',
        borderStyle: 'dashed',
        opacity: cancelled ? 0.6 : 1,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mb: 0.5 }}>
        <AutoAwesome sx={{ fontSize: 16, color: 'secondary.main' }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {isEdit ? t('agent.draft.editTitle') : t('agent.draft.addTitle')}
        </Typography>
        <Chip
          size="small"
          icon={isEdit ? <EditNote sx={{ fontSize: 15 }} /> : <PlaylistAdd sx={{ fontSize: 15 }} />}
          label={entityLabel}
          color="secondary"
          variant="outlined"
          sx={{ height: 20, fontSize: '0.7rem' }}
        />
        {applied && (
          <Chip size="small" color="success" icon={<CheckCircle sx={{ fontSize: 15 }} />}
            label={t('agent.draft.applied')} sx={{ height: 20, fontSize: '0.7rem' }} />
        )}
        {cancelled && (
          <Chip size="small" icon={<Cancel sx={{ fontSize: 15 }} />}
            label={t('agent.draft.cancelled')} sx={{ height: 20, fontSize: '0.7rem' }} />
        )}
      </Box>

      {draft.summary && (
        <Typography variant="body2" sx={{ mb: 0.75 }}>{draft.summary}</Typography>
      )}

      {preview.length > 0 && (
        <>
          <Divider sx={{ my: 0.5 }} />
          <Stack spacing={0.25} sx={{ my: 0.5 }}>
            {preview.map((p, i) => (
              <Box key={i} sx={{ display: 'flex', gap: 1, fontSize: '0.8rem' }}>
                <Typography variant="caption" sx={{ minWidth: 96, color: 'text.secondary', fontWeight: 600 }}>
                  {p.label}
                </Typography>
                <Typography variant="caption" sx={{ wordBreak: 'break-word' }}>{p.val}</Typography>
              </Box>
            ))}
          </Stack>
        </>
      )}

      {!applied && !cancelled && (
        <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap">
          <Button size="small" variant="contained" color="secondary" startIcon={<AutoAwesome sx={{ fontSize: 16 }} />}
            onClick={() => setOpen(true)}>
            {t('agent.draft.review')}
          </Button>
          <Button size="small" color="inherit" startIcon={<Cancel sx={{ fontSize: 16 }} />} onClick={onCancelled}>
            {t('agent.draft.discard')}
          </Button>
          <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
            {t('agent.draft.hint')}
          </Typography>
        </Stack>
      )}

      {applied && draft.route && (
        <Box sx={{ mt: 1 }}>
          <Tooltip title={t('agent.draft.view')}>
            <Button size="small" variant="outlined" startIcon={<OpenInNew sx={{ fontSize: 16 }} />} onClick={goToRecord}>
              {t('agent.draft.view')}
            </Button>
          </Tooltip>
        </Box>
      )}

      {draft.error && (
        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>{draft.error}</Typography>
      )}

      <DetailDialog
        open={open}
        onClose={() => setOpen(false)}
        tableName={draft.entity}
        recordId={isEdit ? (draft.id ?? undefined) : undefined}
        initialData={draft.fields}
        changeSource="agent"
        fields={fields as any}
        onSave={() => { /* handled via onSaved */ }}
        onSaved={(row) => { setOpen(false); onApplied(row); }}
      />
    </Paper>
  );
};

export default AgentDraftCard;
